import type { MapMouseEvent, ViewState } from 'react-map-gl/mapbox'
import type { IAreaBoundary } from '@/shared/api/types/destination-types'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Map, { Layer, Marker, NavigationControl, Source } from 'react-map-gl/mapbox'
import { createBoundaryGeoJson } from '@/features/map-drawing'
import { GEOCODE_SEARCH_DEBOUNCE_MS, POI_MAP_DEFAULT_ZOOM } from '@/shared/constants/map'
import { UI_STATUS_COLORS, UI_THEME_COLORS } from '@/shared/constants/theme-colors'
import { useDebounce } from '@/shared/hooks/use-debounce'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import 'mapbox-gl/dist/mapbox-gl.css'

interface PoiLocationValue {
  latitude: number
  longitude: number
  range: number
  address: string
}

interface DestinationCenter {
  latitude: number
  longitude: number
}

interface PoiMapPickerProps {
  value: PoiLocationValue
  onChange: (value: PoiLocationValue) => void
  destinationAreaBoundary?: IAreaBoundary | null
  destinationCenter?: DestinationCenter | null
}

interface GeocodeFeature {
  id: string
  place_name: string
  center: [number, number]
}

interface SearchOption {
  value: string
  label: string
  latitude: number
  longitude: number
  address: string
}

function toCoordinate(value: number, decimals = 6): number {
  return Number(value.toFixed(decimals))
}

function toRadius(value: number): number {
  return Math.max(1, Number(value))
}

function createGeoJsonCircle(
  latitude: number,
  longitude: number,
  radiusMeters: number,
  points = 96,
) {
  const distanceX = radiusMeters / 1000 / (111.32 * Math.cos((latitude * Math.PI) / 180))
  const distanceY = radiusMeters / 1000 / 110.574

  const coordinates = Array.from({ length: points }, (_, index) => {
    const theta = (index / points) * (2 * Math.PI)
    const x = distanceX * Math.cos(theta)
    const y = distanceY * Math.sin(theta)
    return [longitude + x, latitude + y]
  })

  const firstCoord = coordinates[0]
  if (firstCoord) coordinates.push(firstCoord)

  return {
    type: 'FeatureCollection' as const,
    features: [
      {
        type: 'Feature' as const,
        properties: {},
        geometry: {
          type: 'Polygon' as const,
          coordinates: [coordinates],
        },
      },
    ],
  }
}

export function PoiMapPicker({
  value,
  onChange,
  destinationAreaBoundary = null,
  destinationCenter = null,
}: PoiMapPickerProps) {
  const { t } = useTranslation()
  const mapboxToken = import.meta.env.VITE_MAPBOX_TOKEN
  const mapStyle = import.meta.env.VITE_MAPBOX_STYLE || 'mapbox://styles/mapbox/streets-v12'

  // Input states for coordinates (local strings to allow intermediate typing states)
  const [latInput, setLatInput] = useState(value.latitude.toString())
  const [lngInput, setLngInput] = useState(value.longitude.toString())

  const [searchText, setSearchText] = useState('')
  const [searchOptions, setSearchOptions] = useState<SearchOption[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const debouncedSearchKeyword = useDebounce(searchText, GEOCODE_SEARCH_DEBOUNCE_MS)

  const [viewState, setViewState] = useState<ViewState>({
    latitude: value.latitude,
    longitude: value.longitude,
    zoom: POI_MAP_DEFAULT_ZOOM,
    bearing: 0,
    pitch: 0,
    padding: { top: 0, right: 0, bottom: 0, left: 0 },
  })

  // Sync local input fields when parent value changes (e.g., from map click or search)
  useEffect(() => {
    setLatInput(value.latitude.toString())
    setLngInput(value.longitude.toString())
  }, [value.latitude, value.longitude])

  // Keep map center in sync when form value is hydrated (e.g. edit mode).
  useEffect(() => {
    setViewState((current) => ({
      ...current,
      latitude: value.latitude,
      longitude: value.longitude,
    }))
  }, [value.latitude, value.longitude])

  const radiusGeoJson = useMemo(
    () => createGeoJsonCircle(value.latitude, value.longitude, value.range),
    [value.latitude, value.longitude, value.range],
  )
  const destinationBoundaryGeoJson = useMemo(
    () => createBoundaryGeoJson(destinationAreaBoundary),
    [destinationAreaBoundary],
  )

  useEffect(() => {
    if (!destinationCenter) {
      return
    }

    setViewState((current) => ({
      ...current,
      latitude: destinationCenter.latitude,
      longitude: destinationCenter.longitude,
      zoom: Math.max(current.zoom, POI_MAP_DEFAULT_ZOOM),
    }))
  }, [destinationCenter])

  async function reverseGeocodeAddress(
    latitude: number,
    longitude: number,
  ): Promise<string | null> {
    if (!mapboxToken) {
      return null
    }

    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(`${longitude},${latitude}`)}.json?access_token=${encodeURIComponent(mapboxToken)}&limit=1`,
      )
      const data = (await response.json()) as { features?: GeocodeFeature[] }
      const firstFeature = data.features?.[0]
      if (!firstFeature?.place_name) {
        return null
      }
      return firstFeature.place_name
    } catch {
      return null
    }
  }

  useEffect(() => {
    const trimmedKeyword = debouncedSearchKeyword.trim()
    if (!trimmedKeyword || !mapboxToken) {
      setSearchOptions([])
      setIsSearching(false)
      if (!trimmedKeyword) {
        setShowSuggestions(false)
      }
      return
    }

    const controller = new AbortController()
    setIsSearching(true)
    setShowSuggestions(true)

    void (async () => {
      try {
        const response = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(trimmedKeyword)}.json?access_token=${encodeURIComponent(mapboxToken)}&autocomplete=true&limit=5`,
          { signal: controller.signal },
        )
        const data = (await response.json()) as { features?: GeocodeFeature[] }
        const nextOptions = (data.features ?? []).map((feature) => ({
          value: feature.place_name,
          label: feature.place_name,
          longitude: feature.center[0],
          latitude: feature.center[1],
          address: feature.place_name,
        }))
        setSearchOptions(nextOptions)
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          return
        }
        setSearchOptions([])
      } finally {
        if (!controller.signal.aborted) {
          setIsSearching(false)
        }
      }
    })()

    return () => {
      controller.abort()
    }
  }, [debouncedSearchKeyword, mapboxToken])

  function handleSelectOption(option: SearchOption) {
    onChange({
      ...value,
      latitude: toCoordinate(option.latitude),
      longitude: toCoordinate(option.longitude),
      address: option.address,
    })
    setSearchText(option.label)
    setShowSuggestions(false)
    setViewState((current) => ({
      ...current,
      latitude: option.latitude,
      longitude: option.longitude,
      zoom: Math.max(current.zoom, 15),
    }))
  }

  function handleMapClick(event: MapMouseEvent) {
    const latitude = toCoordinate(event.lngLat.lat)
    const longitude = toCoordinate(event.lngLat.lng)
    onChange({ ...value, latitude, longitude })
    setViewState((current) => ({ ...current, latitude, longitude }))

    void (async () => {
      const resolvedAddress = await reverseGeocodeAddress(latitude, longitude)
      if (resolvedAddress) {
        onChange({ ...value, latitude, longitude, address: resolvedAddress })
      }
    })()
  }

  return (
    <div className="flex flex-col gap-4">
      {!mapboxToken && (
        <div className="flex items-start gap-2 p-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-sm">
          <span className="font-medium">{t('poi.map.missingTokenTitle')}</span>
          <span>{t('poi.map.missingTokenDescription')}</span>
        </div>
      )}

      <div className="border border-zinc-200 rounded-xl overflow-hidden">
        {mapboxToken ? (
          <div className="relative h-[360px] lg:h-[460px]">
            {/* Search autocomplete */}
            <div style={{ position: 'absolute', top: 12, left: 12, right: 72, zIndex: 1 }}>
              <div className="relative w-full max-w-md">
                <Input
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onFocus={() => {
                    if (searchText.trim().length > 0) {
                      setShowSuggestions(true)
                    }
                  }}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                  placeholder={t('poi.map.searchPlaceholder')}
                  className="bg-white shadow-sm"
                />
                {showSuggestions && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-zinc-200 rounded-lg shadow-lg z-10 overflow-hidden">
                    {isSearching && (
                      <div className="px-3 py-2 text-sm text-zinc-400">
                        {t('poi.map.searching')}
                      </div>
                    )}
                    {!isSearching &&
                      searchOptions.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          className="w-full text-left px-3 py-2 text-sm hover:bg-zinc-50 truncate"
                          onMouseDown={() => handleSelectOption(option)}
                        >
                          {option.label}
                        </button>
                      ))}
                    {!isSearching && searchOptions.length === 0 && (
                      <div className="px-3 py-2 text-sm text-zinc-400">
                        {t('poi.map.noResults')}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <Map
              mapboxAccessToken={mapboxToken}
              mapStyle={mapStyle}
              longitude={viewState.longitude}
              latitude={viewState.latitude}
              zoom={viewState.zoom}
              bearing={viewState.bearing}
              pitch={viewState.pitch}
              onMove={(event) => setViewState(event.viewState)}
              onClick={handleMapClick}
              reuseMaps
              style={{ width: '100%', height: '100%' }}
            >
              <NavigationControl position="top-right" />
              {destinationBoundaryGeoJson && (
                <Source id="destination-boundary" type="geojson" data={destinationBoundaryGeoJson}>
                  <Layer
                    id="destination-boundary-fill"
                    type="fill"
                    paint={{ 'fill-color': '#22C55E', 'fill-opacity': 0.12 }}
                  />
                  <Layer
                    id="destination-boundary-outline"
                    type="line"
                    layout={{ 'line-join': 'round', 'line-cap': 'round' }}
                    paint={{ 'line-color': '#16A34A', 'line-width': 2, 'line-dasharray': [2, 1] }}
                  />
                </Source>
              )}
              <Source id="poi-radius" type="geojson" data={radiusGeoJson}>
                <Layer
                  id="poi-radius-fill"
                  type="fill"
                  paint={{ 'fill-color': '#60A5FA', 'fill-opacity': 0.35 }}
                />
                <Layer
                  id="poi-radius-outline"
                  type="line"
                  layout={{ 'line-join': 'round', 'line-cap': 'round' }}
                  paint={{ 'line-color': UI_THEME_COLORS.poiMapRadiusBorder, 'line-width': 2 }}
                />
              </Source>
              <Marker longitude={value.longitude} latitude={value.latitude} anchor="center">
                <div
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    background: UI_STATUS_COLORS.danger,
                    border: `3px solid ${UI_THEME_COLORS.white}`,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                  }}
                />
              </Marker>
            </Map>
          </div>
        ) : (
          <div
            style={{
              height: 360,
              display: 'grid',
              placeItems: 'center',
              background: UI_THEME_COLORS.poiMapGradient,
            }}
          >
            <p className="text-sm text-zinc-500">{t('poi.locationHint')}</p>
          </div>
        )}
      </div>

      <p className="text-xs text-zinc-400">{t('poi.locationHint')}</p>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-6">
        <div className="flex flex-col gap-1.5">
          <Label>{t('poi.radiusMeters')}</Label>
          <Input
            type="number"
            min={1}
            step={1}
            value={value.range}
            onChange={(e) => onChange({ ...value, range: toRadius(Number(e.target.value)) })}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>{t('poi.latitude')}</Label>
          <Input
            type="number"
            min={-90}
            max={90}
            step="any"
            value={latInput}
            onChange={(e) => {
              const strVal = e.target.value
              setLatInput(strVal)
              const lat = Number.parseFloat(strVal)
              if (!Number.isNaN(lat) && lat >= -90 && lat <= 90) {
                onChange({ ...value, latitude: lat })
                setViewState((current) => ({ ...current, latitude: lat }))
              }
            }}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>{t('poi.longitude')}</Label>
          <Input
            type="number"
            min={-180}
            max={180}
            step="any"
            value={lngInput}
            onChange={(e) => {
              const strVal = e.target.value
              setLngInput(strVal)
              const lng = Number.parseFloat(strVal)
              if (!Number.isNaN(lng) && lng >= -180 && lng <= 180) {
                onChange({ ...value, longitude: lng })
                setViewState((current) => ({ ...current, longitude: lng }))
              }
            }}
          />
        </div>
        <div className="flex flex-col gap-1.5 lg:col-span-3">
          <Label>{t('poi.address')}</Label>
          <Input
            value={value.address ?? ''}
            onChange={(e) => onChange({ ...value, address: e.target.value })}
            placeholder={t('poi.address')}
          />
        </div>
      </div>
    </div>
  )
}
